import React from 'react'
import useTranslation from 'next-translate/useTranslation'
import clsx from 'clsx'
import { ErrorCode } from '@ancon/wildcat-types'

import Button from '../Button'
import useAppSelector from '../../../../store/hooks/useAppSelector'
import { appErrorModalConfigSelector } from '../../store/appSelectors'
import useAppDispatch from '../../../../store/hooks/useAppDispatch'
import { appSetIsErrorModalVisible } from '../../store/appSlice'
import BodyText from '../BodyText'
import { AppTestIds } from '../../constants'
import { ErrorModalType, InformativeError } from '../../types'
import { setProductDetailsModalVisible } from '../../../product/store/productSlice'
import storedPaymentCorrelationId from '../../utils/storedPaymentCorrelationId'
import {
  checkoutRetryAdyenPayment,
  checkoutSetIsVisibleBoxPickupScheduleOrderModal,
} from '../../../checkout/store/checkoutSlice'
import {
  checkRetryPayment,
  setIsCheckPaymentInProgress,
} from '../../../check/store/checkSlice'
import {
  outletForceRefreshMenuIfNeeded,
  outletSetOrderConfigureFilterModal,
} from '../../../outlet/store/outletSlice'
import { OrderConfigureFilterMode } from '../../../outlet/types'
import { validateCheckout } from '../../../checkout/store/checkoutThunks'
import { setPreOrderTimeChangeModalVisible } from '../../../preOrder/store/preOrderSlice'
import { setPendingSignOutCheckoutUpdate } from '../../../auth/store/authSlice'
import { setPendingCompanyProfileSwitchMemberId } from '../../../company/store/companySlice'
import useCheckoutPriceChangePaymentRetry from '../../../checkout/hooks/useCheckoutPriceChangePaymentRetry'
import usePayAndGoMergeBack from '../../../payAndGo/hooks/usePayAndGoMergeBack'

import styles from './ErrorModalFooter.module.scss'
import { useDeleteOverrideMenuConflicts } from './hooks/useDeleteOverrideMenuConflicts'
import { useDeleteInvalidCheckoutItems } from './hooks/useDeleteInvalidCheckoutItems'

export default function ErrorModalFooter() {
  const handleDeleteOverrideMenuConflictCheckoutItems =
    useDeleteOverrideMenuConflicts()

  const deleteInvalidCheckoutItems = useDeleteInvalidCheckoutItems()

  const dispatch = useAppDispatch()

  const retryPaymentAfterPriceChange = useCheckoutPriceChangePaymentRetry()

  const { t } = useTranslation('common')

  const errorModalConfig = useAppSelector(appErrorModalConfigSelector)

  const mergeBack = usePayAndGoMergeBack()

  async function handleRequestClose() {
    switch (errorModalConfig?.errorModalType) {
      case ErrorModalType.SurfboardValidationError:
        dispatch(setIsCheckPaymentInProgress(false))
        break
      case ErrorModalType.PayAndGoSessionExpired:
        mergeBack()
        break
      case ErrorModalType.ProductOutOfStock:
        dispatch(setProductDetailsModalVisible(false))
        break
      case ErrorModalType.CheckoutPaymentError:
        storedPaymentCorrelationId.refresh()
        dispatch(checkoutRetryAdyenPayment())
        break
      case ErrorModalType.CheckoutPaymentPriceChanged:
        retryPaymentAfterPriceChange()
        break
      case ErrorModalType.CheckPaymentError:
        storedPaymentCorrelationId.refresh()
        dispatch(checkRetryPayment())
        break
      case ErrorModalType.CheckoutUnavailableProductsDueToOverrideMenu:
        dispatch(setPendingCompanyProfileSwitchMemberId(null))
        dispatch(setPendingSignOutCheckoutUpdate(false))
        break
      case ErrorModalType.CheckoutItemsNotValid:
        // Drop the now-unavailable item(s) before refreshing, otherwise the same error repeats
        await deleteInvalidCheckoutItems()
        dispatch(outletForceRefreshMenuIfNeeded())
        dispatch(validateCheckout({}))
        break
      default:
    }

    dispatch(appSetIsErrorModalVisible(false))
  }

  async function handlePrimaryAction() {
    switch (errorModalConfig?.errorModalType) {
      case ErrorModalType.CheckoutBoxPickupUnAvailableProducts:
        dispatch(checkoutSetIsVisibleBoxPickupScheduleOrderModal(true))
        break
      case ErrorModalType.CheckoutUnavailableOrderTime: {
        const orderTimeErrorModalConfig =
          errorModalConfig as InformativeError<ErrorCode.CheckoutUnavailableOrderTime>
        const isPreOrderCheckout =
          !!orderTimeErrorModalConfig?.additionalData?.isPreOrderCheckout

        if (isPreOrderCheckout) {
          dispatch(setPreOrderTimeChangeModalVisible(true))
        } else {
          dispatch(
            outletSetOrderConfigureFilterModal(
              OrderConfigureFilterMode.OrderTime,
            ),
          )
        }

        break
      }
      case ErrorModalType.CheckoutUnavailableProductsDueToOverrideMenu:
        await handleDeleteOverrideMenuConflictCheckoutItems()
        dispatch(setProductDetailsModalVisible(false))
        break
      default:
    }

    dispatch(appSetIsErrorModalVisible(false))
  }

  switch (errorModalConfig?.errorModalType) {
    case ErrorModalType.PayAndGoSessionExpired:
      return (
        <Button
          size="large"
          className={styles.button}
          onClick={handleRequestClose}
        >
          <BodyText className={styles.buttonText}>
            {t('payAndGo:sessionExpiredAlert.button')}
          </BodyText>
        </Button>
      )

    case ErrorModalType.CheckoutItemPriceChanged:
    case ErrorModalType.CheckoutPaymentPriceChanged:
      return (
        <Button
          size="large"
          className={styles.button}
          onClick={handleRequestClose}
        >
          <BodyText className={styles.buttonText}>{t('okay')}</BodyText>
        </Button>
      )
    case ErrorModalType.ProductOutOfStock:
    case ErrorModalType.OutletNotAvailableForOrders:
    case ErrorModalType.CheckoutFetchError:
    case ErrorModalType.CheckoutAddTipError:
    case ErrorModalType.SurfboardValidationError:
      return (
        <Button
          size="large"
          className={styles.button}
          onClick={handleRequestClose}
        >
          <BodyText className={styles.buttonText}>{t('close')}</BodyText>
        </Button>
      )
    case ErrorModalType.CheckoutBoxPickupUnAvailableProducts:
      return (
        <>
          <Button
            size="large"
            variant="secondary"
            className={styles.button}
            onClick={handleRequestClose}
          >
            <BodyText className={clsx(styles.buttonText, styles.secondary)}>
              {t('retry')}
            </BodyText>
          </Button>
          <Button
            size="large"
            className={styles.button}
            onClick={handlePrimaryAction}
          >
            <BodyText className={styles.buttonText}>
              {t('boxPickup.changeTimeSlot')}
            </BodyText>
          </Button>
        </>
      )
    case ErrorModalType.CheckoutUnavailableOrderTime:
      return (
        <>
          <Button
            size="large"
            variant="secondary"
            className={styles.button}
            onClick={handleRequestClose}
          >
            <BodyText className={clsx(styles.buttonText, styles.secondary)}>
              {t('retry')}
            </BodyText>
          </Button>
          <Button
            size="large"
            className={styles.button}
            onClick={handlePrimaryAction}
          >
            <BodyText className={styles.buttonText}>
              {t('checkout.changeOrderTime')}
            </BodyText>
          </Button>
        </>
      )
    case ErrorModalType.CheckoutUnavailableProductsDueToOverrideMenu:
      return (
        <>
          <Button
            size="large"
            className={styles.button}
            onClick={handleRequestClose}
          >
            <BodyText className={styles.buttonText}>{t('retry')}</BodyText>
          </Button>
          <Button
            size="large"
            className={styles.button}
            onClick={handlePrimaryAction}
          >
            <BodyText className={styles.buttonText}>
              {t('errors.unAvailableItemDueToOverrideMenu.removeAll')}
            </BodyText>
          </Button>
        </>
      )
    default:
      return (
        <Button
          size="large"
          className={styles.button}
          onClick={handleRequestClose}
          data-testid={AppTestIds.ErrorModalPrimaryButton}
        >
          <BodyText className={styles.buttonText}>{t('retry')}</BodyText>
        </Button>
      )
  }
}
